import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { WORKFORCE_PERSONAS } from "../../../../../../convex/aiWorkforce/config";
import { getPersonaTheme } from "../personaTheme";
import {
  FileCode,
  Copy,
  Check,
  Download,
  CheckCircle2,
  XCircle,
  Clock,
  Sparkles,
} from "lucide-react";

interface MarkdownDeliverableCanvasProps {
  artifact: {
    _id: string;
    title: string;
    type: string;
    createdPersona: string;
    status: string;
    data?: any;
    createdAt: number;
  };
  onApprove?: () => void;
  onReject?: () => void;
  isProcessing?: boolean;
}

export function MarkdownDeliverableCanvas({
  artifact,
  onApprove,
  onReject,
  isProcessing = false,
}: MarkdownDeliverableCanvasProps) {
  const [copied, setCopied] = useState(false);
  const persona = WORKFORCE_PERSONAS[artifact.createdPersona] || WORKFORCE_PERSONAS.chief_of_staff;
  const theme = getPersonaTheme(persona.key);

  const rawMarkdown =
    artifact.data?.markdown ||
    artifact.data?.content ||
    artifact.data?.report ||
    artifact.data?.text ||
    "";

  const handleCopy = () => {
    if (!rawMarkdown) return;
    navigator.clipboard.writeText(rawMarkdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!rawMarkdown) return;
    const blob = new Blob([rawMarkdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${artifact.title.replace(/[^a-zA-Z0-9_-]/g, "_")}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/40 rounded-2xl overflow-hidden border border-gray-200">
      {/* Header Bar */}
      <div className="p-4 bg-white border-b border-gray-200 flex items-center justify-between gap-4 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0 shadow-2xs">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-bold text-gray-900 truncate">
                {artifact.title}
              </h3>
              <Badge variant="outline" className="text-xs uppercase font-semibold text-purple-700 bg-purple-50 border-purple-200">
                Research Brief
              </Badge>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Prepared by <span className="font-semibold text-gray-700">{persona.name}</span> • {new Date(artifact.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="gap-1.5 text-xs text-gray-700 font-semibold"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copied ? "Copied" : "Copy"}</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            className="gap-1.5 text-xs text-gray-700 font-semibold"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Export .md</span>
          </Button>
        </div>
      </div>

      {/* Markdown Reader Body */}
      <div className="flex-1 overflow-y-auto p-6 sm:p-8 custom-scrollbar">
        <div className="max-w-3xl mx-auto bg-white p-6 sm:p-10 rounded-2xl border border-gray-200 shadow-sm">
          {rawMarkdown ? (
            <div className="prose prose-slate max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-800 prose-p:leading-relaxed prose-table:border prose-th:bg-slate-50 prose-th:p-2 prose-td:p-2 prose-td:border prose-code:text-blue-700 prose-code:bg-blue-50 prose-code:px-1 prose-code:rounded">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {rawMarkdown}
              </ReactMarkdown>
            </div>
          ) : (
            <div className="py-12 text-center text-gray-400 space-y-2">
              <FileCode className="w-8 h-8 mx-auto text-gray-300" />
              <p className="text-sm font-medium">No markdown content available for this deliverable.</p>
            </div>
          )}
        </div>
      </div>

      {/* Approval Footer */}
      {artifact.status === "pending_approval" && onApprove && (
        <div className="p-4 bg-white border-t border-gray-200 flex items-center justify-between gap-4 flex-shrink-0">
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-800 bg-amber-50 px-3 py-1.5 rounded-full border border-amber-200">
            <Clock className="w-4 h-4 text-amber-600" />
            <span>Pending Human Sign-Off</span>
          </div>

          <div className="flex items-center gap-2.5">
            {onReject && (
              <Button
                variant="outline"
                size="sm"
                onClick={onReject}
                disabled={isProcessing}
                className="text-gray-700 gap-1.5 text-xs font-semibold"
              >
                <XCircle className="w-4 h-4 text-rose-500" />
                <span>Decline</span>
              </Button>
            )}
            <Button
              size="sm"
              onClick={onApprove}
              disabled={isProcessing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 text-xs font-semibold shadow-xs"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>Approve Report</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
